import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import { Alert, Spinner } from '@/components/ui/misc';
import { GoogleIcon } from '@/components/GoogleIcon';
import { useAuth } from '@/context/AuthContext';

export function Login() {
  const { signInEmail, signInGoogle, resetPassword, configured } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from ?? '/app';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signInEmail(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(friendly(err));
    } finally {
      setBusy(false);
    }
  }

  async function google() {
    setError(null);
    setBusy(true);
    try {
      await signInGoogle();
      navigate(from, { replace: true });
    } catch (err) {
      setError(friendly(err));
    } finally {
      setBusy(false);
    }
  }

  async function forgot() {
    setError(null);
    setInfo(null);
    if (!email) {
      setError('Enter your email above first, then click reset.');
      return;
    }
    try {
      await resetPassword(email);
      setInfo('If an account exists, a password reset email is on its way.');
    } catch (err) {
      setError(friendly(err));
    }
  }

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your Visiward account">
      {!configured && (
        <Alert tone="error">Authentication is not configured yet. Set the Firebase web keys.</Alert>
      )}
      <Button variant="secondary" className="w-full" onClick={google} disabled={busy || !configured}>
        <GoogleIcon /> Continue with Google
      </Button>
      <div className="my-4 flex items-center gap-3 text-xs text-text-muted">
        <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <button
              type="button"
              onClick={forgot}
              className="mb-1.5 text-xs text-accent-blue hover:text-accent-indigo"
            >
              Forgot password?
            </button>
          </div>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </div>
        {error && <Alert tone="error">{error}</Alert>}
        {info && <Alert tone="success">{info}</Alert>}
        <Button type="submit" className="w-full" disabled={busy || !configured}>
          {busy ? <Spinner /> : 'Sign in'}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-text-secondary">
        Don’t have an account?{' '}
        <Link to="/register" className="link-accent font-medium">
          Create one
        </Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-12">
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-bold tracking-tight text-text-primary">{title}</h1>
        <p className="mt-1 text-sm text-text-secondary">{subtitle}</p>
      </div>
      <div className="panel p-6">{children}</div>
    </div>
  );
}

function friendly(err: unknown): string {
  const code = (err as { code?: string })?.code ?? '';
  if (code.includes('invalid-credential') || code.includes('wrong-password'))
    return 'Incorrect email or password.';
  if (code.includes('user-not-found')) return 'No account found with that email.';
  if (code.includes('too-many-requests')) return 'Too many attempts. Please try again later.';
  if (code.includes('popup-closed')) return 'Sign-in was cancelled.';
  return err instanceof Error ? err.message : 'Something went wrong. Please try again.';
}
